import { EText } from 'data/datas/genericData';
import { TRtkErrorType } from 'data/types/api.types';
import { TPetCount, TRoommateAnimalDB } from 'data/types/roommates.types';
import moment, { Moment } from 'moment';
import toast from 'components/Notification';

export const defaultDateFormat = 'MM/DD/YYYY';
export const serverDateFormat = 'YYYY-MM-DD';
export const dateTimeFormat = 'MM/DD/YYYY hh:mm A';
export const timeFormat = 'HH:mm:ss';
export const displayTimeFormat = 'h:mm A';
export const verificationCodeLength = 6;

export const generateSpaceClassNames = (array: number[]) => {
  let classNames = ``;
  array.forEach((n, i) => {
    classNames += `
            .mx-${i} {
                margin: 0 ${n}px !important;
            }
            .my-${i} {
                margin: ${n}px 0 !important;
            }
            .m-${i} {
                margin: ${n}px !important;
            }
            .mt-${i} {
                margin-top: ${n}px !important;
            }
            .mb-${i} {
                margin-bottom: ${n}px !important;
            }
            .ml-${i} {
                margin-left: ${n}px !important;
            }
            .mr-${i} {
                margin-right: ${n}px !important;
            }
            .pt-${i} {
                padding-top: ${n}px !important;
            }
            .pb-${i} {
                padding-bottom: ${n}px !important;
            }
            .pl-${i} {
                padding-left: ${n}px !important;
            }
            .pr-${i} {
                padding-right: ${n}px !important;
            }
            .px-${i} {
                padding: 0 ${n}px !important;
            }
            .py-${i} {
                padding: ${n}px 0 !important;
            }
            .p-${i} {
                padding: ${n}px !important;
            }
        `;
  });

  return classNames;
};

export const formattedDate = (date?: Moment | string, format?: string) => {
  return moment(date).format(format || defaultDateFormat);
};

export const parseTime = (time: string): Moment => {
  return moment(time, timeFormat);
};

export const formattedTime = (time?: Moment | string, format?: string) => {
  return moment(time, timeFormat).format(format || displayTimeFormat);
};

export const animalsTypeCounter = (data: TRoommateAnimalDB[], accum?: TPetCount) => {
  return data.reduce((data, animal) => {
    if (data[animal.type]) {
      data[animal.type]++;
    } else {
      data[animal.type] = 1;
    }
    return data;
  }, accum || { ESA: 0, PET: 0, SA: 0 });
};

export const formattedDateTimezone = (date: Moment | string, format?: string) => {
  return moment
    .utc(date)
    .local()
    .format(format || defaultDateFormat);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const catchRtkError = (e: any) => {
  toast({
    description: (e as TRtkErrorType)?.data?.message || EText.DEFAULT_ERROR,
    type: 'error',
  });
};

export const formatStateLabel = (input: string) => {
  return input
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const formatToSnakeCase = (input: string) => {
  return input.toUpperCase().replace(/ /g, '_');
};

export const hasOneDayPassed = (dateString: string) => {
  const givenDate = new Date(dateString);
  const currentDate = new Date();

  givenDate.setHours(0, 0, 0, 0);
  currentDate.setHours(0, 0, 0, 0);

  const diffInTime = currentDate.getTime() - givenDate.getTime();
  const diffInDays = diffInTime / (1000 * 60 * 60 * 24);

  return diffInDays >= 1;
};

export function formEntriesToTypedObject(entries: Iterable<[string, string]>): Record<string, any> {
  const obj: Record<string, any> = {};

  for (const [key, value] of entries) {
    let val: any = value;

    if (value === 'true') {
      val = true;
    } else if (value === 'false') {
      val = false;
    } else if (!isNaN(value as any) && value.trim() !== '') {
      val = Number(value);
    }

    if (obj[key] !== undefined) {
      obj[key] = [].concat(obj[key], val);
    } else {
      obj[key] = val;
    }
  }

  return obj;
}

export const hubSpotContactLinkGenerate = (id: string | null) => {
  if (!id) return;
  return `https://app.hubspot.com/contacts/40118531/record/0-1/${id}`;
};

export const hubSpotTicketLinkGenerate = (id: string | null) => {
  if (!id) return;
  return `https://app.hubspot.com/contacts/40118531/record/0-5/${id}`;
};

export const getSpeciesDisplayInfo = (species: { id: string; value: string }) => {
  const isNormalized = !species.id && !!species.value && species.value.toLowerCase() !== 'other';
  return {
    displayText: isNormalized ? 'Other' : species.value || '',
    originalValue: isNormalized ? species.value : null,
    showInfoIcon: isNormalized,
  };
};

export const capitalizeStr = (value: string) => {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};
